import React, { useState, useEffect } from 'react';
import {
  Box, 
  Typography, 
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Avatar,
  Tooltip,
  Button,
  Switch,
  FormControlLabel,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  AccountCircle as AccountIcon,
  DesignServices as DesignServicesIcon,
  Group as GroupIcon,
  Work as WorkIcon,
  Handyman as HandymanIcon,
  Lightbulb as LightbulbIcon,
  Message as MessageIcon,
  Inventory as InventoryIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeContext } from '../contexts/ThemeContext';
import ErrorBoundary from './ErrorBoundary';
import { useMessages } from '../contexts/MessageContext';
import { APP_VERSION, GIT_SHA } from '../version';

const drawerWidth = 240;

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { logout, currentUser } = useAuth();
  const { themeMode, setThemeMode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activePath, setActivePath] = useState(location.pathname);
  const { newMessageCount } = useMessages();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [changelogText, setChangelogText] = useState<string | null>(null);

  const openChangelog = async () => {
    setChangelogOpen(true);
    if (changelogText === null) {
      try {
        const r = await fetch('/changelog.md', { cache: 'no-cache' });
        setChangelogText(r.ok ? await r.text() : 'Changelog unavailable.');
      } catch {
        setChangelogText('Changelog unavailable.');
      }
    }
  };
  
  const isDarkMode = themeMode === 'dark' || (themeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Update active path when location changes
  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setActivePath(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Error logging out:', error);
      navigate('/admin/login');
    }
  };

  const toggleDarkMode = () => {
    setThemeMode(isDarkMode ? 'light' : 'dark');
  };

  const adminNavItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { text: 'Home Page', icon: <HomeIcon />, path: '/admin/home' },
    { text: 'About Page', icon: <InfoIcon />, path: '/admin/about' },
    { text: 'Team Members', icon: <GroupIcon />, path: '/admin/team' },
    { text: 'Portfolio', icon: <WorkIcon />, path: '/admin/portfolio' },
    { text: 'Services', icon: <DesignServicesIcon />, path: '/admin/services' },
    { text: 'Tools Database', icon: <HandymanIcon />, path: '/admin/tools' },
    { text: 'Lighting Database', icon: <LightbulbIcon />, path: '/admin/lighting-database' },
    { 
      text: 'Messages', 
      icon: (
        <Badge badgeContent={newMessageCount} color="error" sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }}>
          <MessageIcon />
        </Badge>
      ), 
      path: '/admin/messages' 
    },
    { text: 'Rental', icon: <InventoryIcon />, path: '/admin/rental' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
  ];

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: isDarkMode ? '#4CAF50' : undefined }}>
          Admin Panel
        </Typography>
      </Box>
      {currentUser && (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main, mb: 1, width: 40, height: 40 }}>
            {currentUser.email ? currentUser.email[0].toUpperCase() : 'A'}
          </Avatar>
          <Typography variant="body2" align="center" noWrap>
            {currentUser.email}
          </Typography>
        </Box>
      )}
      <Divider />
      <List sx={{ flexGrow: 1, overflow: 'auto' }}>
        {adminNavItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={activePath === item.path}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText 
                primary={item.text} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={isDarkMode}
              onChange={toggleDarkMode}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DarkModeIcon sx={{ mr: 1, fontSize: '1.2rem', color: isDarkMode ? '#4CAF50' : undefined }} />
              <Typography variant="body2">Dark Mode</Typography>
            </Box>
          }
          sx={{ mb: 1 }}
        />
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon><LogoutIcon /></ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
        <Box sx={{ mt: 0.75, textAlign: 'center' }}>
          <Link
            component="button"
            type="button"
            underline="none"
            onClick={openChangelog}
            title={`View changelog · v${APP_VERSION} · ${GIT_SHA}`}
            sx={{
              fontSize: '8px',
              fontWeight: 500,
              letterSpacing: '0.04em',
              fontVariantNumeric: 'tabular-nums',
              color: 'text.disabled',
              opacity: 0.55,
              cursor: 'pointer',
              fontFamily: 'inherit',
              lineHeight: 1.2,
              border: 'none',
              background: 'none',
              p: 0,
              '&:hover': {
                opacity: 0.9,
                color: 'text.secondary',
                textDecoration: 'underline',
              },
            }}
          >
            v{APP_VERSION}
          </Link>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: isDarkMode ? '#1E1E1E' : undefined
        }}
        elevation={0}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: isDarkMode ? '#4CAF50' : undefined }}>
            Paragon Live Design - Admin
          </Typography>
          {currentUser && (
            <Tooltip title={currentUser.email || ''}>
              <IconButton color="inherit" sx={{ mr: 1 }}>
                <AccountIcon />
              </IconButton>
            </Tooltip>
          )}
          <Button color="inherit" onClick={handleLogout} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px', // Toolbar height
        }}
      >
        <ErrorBoundary>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </ErrorBoundary>
      </Box>

      <Dialog open={changelogOpen} onClose={() => setChangelogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Changelog — v{APP_VERSION} ({GIT_SHA})</DialogTitle>
        <DialogContent dividers>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 13, fontFamily: 'inherit', m: 0 }}>
            {changelogText ?? 'Loading…'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangelogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLayout; 